import React, { useState, ChangeEvent, FormEvent } from 'react';
import axios from 'axios';
import { UpdateFormData, UpdateStatus, UpdateResponse } from '@/types';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export const UploadUpdate: React.FC = () => {
  const [formData, setFormData] = useState<UpdateFormData>({
    version: '',
    releaseNotes: '',
    minAppVersion: '',
    file: null
  });
  const [status, setStatus] = useState<UpdateStatus>({ type: '', message: '' });
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.name.endsWith('.ex4')) {
      setFormData(prev => ({
        ...prev,
        file
      }));
      setStatus({ type: '', message: '' });
    } else {
      setStatus({
        type: 'danger',
        message: 'Please select a valid .ex4 file'
      });
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsUploading(true);
    setStatus({ type: '', message: '' });

    try {
      // Validate version format
      if (!/^\d+\.\d+\.\d+$/.test(formData.version)) {
        throw new Error('Version must be in format X.Y.Z');
      }

      const data = new FormData();
      data.append('version', formData.version);
      data.append('releaseNotes', formData.releaseNotes);
      if (formData.file) {
        data.append('indicator', formData.file);
      }
      if (formData.minAppVersion) {
        data.append('minAppVersion', formData.minAppVersion);
      }

      const response = await axios.post<UpdateResponse>('/updates/publish', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${process.env.REACT_APP_API_KEY}`
        }
      });

      console.log(response);

      setStatus({
        type: 'success',
        message: `Successfully published version ${formData.version}`
      });

      // Clear form
      setFormData({
        version: '',
        releaseNotes: '',
        minAppVersion: '',
        file: null
      });

    } catch (error) {
      if (axios.isAxiosError(error)) {
        setStatus({
          type: 'danger',
          message: error.response?.data?.message || error.message
        });
      } else {
        setStatus({
          type: 'danger',
          message: error instanceof Error ? error.message : 'An unknown error occurred'
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">Upload New Update</h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Upload Details</CardTitle>
        </CardHeader>
        <CardContent>
          {status.message && (
            <Alert variant={status.type as "default" | "destructive"} className="mb-6">
              <AlertDescription>{status.message}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="version">Version Number</Label>
                <Input
                  id="version"
                  name="version"
                  value={formData.version}
                  onChange={handleInputChange}
                  placeholder="e.g., 1.0.1"
                  required
                />
                <p className="text-sm text-muted-foreground">
                  Use semantic versioning (X.Y.Z)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="releaseNotes">Release Notes</Label>
                <Textarea
                  id="releaseNotes"
                  name="releaseNotes"
                  value={formData.releaseNotes}
                  onChange={handleInputChange}
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="minAppVersion">Minimum App Version</Label>
                <Input
                  id="minAppVersion"
                  name="minAppVersion"
                  value={formData.minAppVersion}
                  onChange={handleInputChange}
                  placeholder="e.g., 1.0.0 (optional)"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="file">Indicator File (.ex4)</Label>
                <Input
                  id="file"
                  type="file"
                  accept=".ex4"
                  onChange={handleFileChange}
                  required
                  className="cursor-pointer"
                />
              </div>
            </div>

            <Button 
              type="submit"
              disabled={isUploading}
              className="w-full sm:w-auto"
            >
              {isUploading ? 'Uploading...' : 'Upload Update'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};